import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ICompanyDTO, ICompanyNameIdLegalFormDTO, ICompanyStatusDTO } from '@modeso/types__aumico-lib-companies-be';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import Debug from 'debug';
import saveAs from 'file-saver';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { FileMetadataService } from '../../../../aumico-project-reports/src/app/microservices/ms-annual-reports/services/documents.service';
import * as fromActions from '../actions/companies.actions';
import { ICompanyExportDto } from '../dtos/company-export-dto.interface';
import { ICompanyExportTableHeaders } from '../dtos/company-export-table-header-dto.interface';
import { TasksUpdate } from '../enums/tasks-update.enum';
import { AumicoCompaniesService } from '../services/companies.service';
const debug = Debug('aumico:aumico-lib-companies-fe:CompaniesEffects');

@Injectable()
export class CompaniesEffects {
    constructor(
        private actions$: Actions,
        private service: AumicoCompaniesService,
        private readonly fileMetadataService: FileMetadataService,
    ) {}

    createCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.createCompany),
            mergeMap((action) => {
                const { logo, company } = action;
                if (logo != null) {
                    return this.service.uploadCompanyLogo(logo).pipe(
                        switchMap((fileMetadata) => {
                            company.logo = {
                                uuid: fileMetadata.uuid,
                                name: fileMetadata.name,
                                extension: fileMetadata.extension,
                                mimeType: fileMetadata.mimeType,
                                size: fileMetadata.size,
                            };

                            return this.createCompanyThenActivateFx(company, action.activateFx);
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(fromActions.onCreateCompanyFailed({ payload: error }));
                        }),
                    );
                } else {
                    company.logo = null;
                    return this.createCompanyThenActivateFx(action.company, action.activateFx).pipe(
                        catchError((error: HttpErrorResponse) => {
                            return of(fromActions.onCreateCompanyFailed({ payload: error }));
                        }),
                    );
                }
            }),
        ),
    );

    /**
     * Creates the company and, when the functional-currency terms were accepted while creating,
     * activates the entitlement right after — activation is bound to the company id, which only
     * exists now. Running it here rather than in the page keeps it alive across the navigation
     * that follows a successful creation.
     *
     * A failing activation does NOT fail the creation: the company exists either way and can be
     * activated on its profile page, where the error would otherwise be unrecoverable.
     */
    private createCompanyThenActivateFx(company: ICompanyDTO, activateFx: boolean | undefined) {
        return this.service.createCompany(company).pipe(
            switchMap((created: ICompanyDTO) =>
                activateFx === true && created._id != null
                    ? this.service.activateFx(created._id).pipe(catchError(() => of(created)))
                    : of(created),
            ),
            map((created: ICompanyDTO) => {
                debug(created);
                return fromActions.onCreateCompanySuccessfully({ payload: { company: created } });
            }),
        );
    }

    updateCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateCompany.type),
            mergeMap((action: { company: ICompanyDTO; logo: File | null; tasksUpdate?: TasksUpdate }) => {
                const { logo, company, tasksUpdate } = action;
                if (logo != null) {
                    return this.service.uploadCompanyLogo(logo).pipe(
                        switchMap((fileMetadata) => {
                            company.logo = {
                                uuid: fileMetadata.uuid,
                                name: fileMetadata.name,
                                extension: fileMetadata.extension,
                                mimeType: fileMetadata.mimeType,
                                size: fileMetadata.size,
                            };

                            return this.service.updateCompany(company, tasksUpdate).pipe(
                                map((response) => {
                                    return fromActions.onUpdateCompanySuccessfully({ payload: response });
                                }),
                            );
                        }),
                        catchError((error) => {
                            return of(fromActions.onUpdateCompanyFailed({ payload: error }));
                        }),
                    );
                } else {
                    company.logo = null;
                    return this.service.updateCompany(company, tasksUpdate).pipe(
                        map((response) => {
                            return fromActions.onUpdateCompanySuccessfully({ payload: response });
                        }),
                        catchError((error) => {
                            return of(fromActions.onUpdateCompanyFailed({ payload: error }));
                        }),
                    );
                }
            }),
        ),
    );

    bulkUpdateCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.bulkUpdateCompanies.type),
            mergeMap((action: { companies: ICompanyDTO[]; logo: File | null; tasksUpdate?: TasksUpdate }) => {
                const { logo, companies, tasksUpdate } = action;
                if (logo != null) {
                    return this.service.uploadCompanyLogo(logo).pipe(
                        switchMap((fileMetadata) => {
                            const logoData = {
                                uuid: fileMetadata.uuid,
                                name: fileMetadata.name,
                                extension: fileMetadata.extension,
                                mimeType: fileMetadata.mimeType,
                                size: fileMetadata.size,
                            };

                            return this.service.bulkUpdateCompanies(companies, logoData, tasksUpdate).pipe(
                                map((response) => {
                                    return fromActions.onBulkUpdateCompaniesSuccessfully({ payload: response });
                                }),
                            );
                        }),
                        catchError((error) => {
                            return of(fromActions.onBulkUpdateCompaniesFailed({ payload: error }));
                        }),
                    );
                } else {
                    return this.service.bulkUpdateCompanies(companies, null, tasksUpdate).pipe(
                        map((response) => {
                            return fromActions.onBulkUpdateCompaniesSuccessfully({ payload: response });
                        }),
                        catchError((error) => {
                            return of(fromActions.onBulkUpdateCompaniesFailed({ payload: error }));
                        }),
                    );
                }
            }),
        ),
    );

    getCompanyLogo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getCompanyLogo.download.type),
            mergeMap((action) => {
                const { documentId } = action;
                return this.fileMetadataService.downloadDocument$(documentId).pipe(
                    map((response) => {
                        return fromActions.getCompanyLogo.downloadsuccess({ payload: response });
                    }),
                    catchError((error) => {
                        return of(fromActions.getCompanyLogo.downloadfailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    changeCompanyStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.changeStatus.type),
            mergeMap((action: fromActions.ActionWithPayload<ICompanyStatusDTO>) => {
                return this.service.updateCompanyStatus(action.payload).pipe(
                    map((response) => {
                        return fromActions.onChangeStatusSuccessfully({ payload: response });
                    }),
                    catchError((error) => {
                        return of(fromActions.onChangeStatusFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    deleteCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.deleteCompany.type),
            mergeMap((action: fromActions.ActionWithPayload<ICompanyStatusDTO>) => {
                return this.service.deleteCompany(action.payload).pipe(
                    map((response) => {
                        return fromActions.onDeleteCompanySuccessfully({ payload: response });
                    }),
                    catchError((error) => {
                        return of(fromActions.onDeleteCompanyFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getCompanies.type),
            mergeMap(() => {
                return this.service.getCompanies().pipe(
                    map((response: ICompanyDTO[]) => {
                        debug(response);
                        return fromActions.onGettingCompaniesSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.onGettingCompaniesFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getCompaniesNameIdLegalForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getCompaniesNameIdLegalForm.type),
            mergeMap(() => {
                return this.service.getCompaniesNameIdLegalForm().pipe(
                    map((response: ICompanyNameIdLegalFormDTO[]) => {
                        debug(response);
                        return fromActions.onGettingCompaniesNameIdLegalFormSuccessfully({ payload: response });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.onGettingCompaniesNameIdLegalFormFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    getCompanyById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.getCompanyById.type),
            mergeMap((action: fromActions.ActionWithPayload<string>) => {
                return this.service.getCompanyById(action.payload).pipe(
                    map((response) => {
                        return fromActions.onGettingCompanyByIdSuccessfully({ payload: response });
                    }),
                    catchError((error) => {
                        return of(fromActions.onGettingCompanyByIdFailed({ payload: error }));
                    }),
                );
            }),
        ),
    );

    companyValuation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.companyValuation),
            mergeMap((action) => {
                return this.service.companyValuation(action.payload).pipe(
                    map((response) => {
                        debug(response);
                        return fromActions.onCompanyValuationSuccess();
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(fromActions.onCompanyValuationFailure({ payload: error }));
                    }),
                );
            }),
        ),
    );

    downloadDefaultCompanyValuationFile = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.downloadDefaultCompanyValuationFile),
            mergeMap(() => {
                return this.service.downloadDefaultValuationFile$().pipe(
                    map((response) => {
                        const blob = new Blob([response], {
                            type: 'application/pdf',
                        });
                        saveAs(blob, 'Muster Steuerwert Berechnung.pdf');
                        return fromActions.onDownloadDefaultCompanyValuationFileSuccessfully({
                            payload: response,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(
                            fromActions.onDownloadDefaultCompanyValuationFileFailed({
                                payload: error,
                            }),
                        );
                    }),
                );
            }),
        ),
    );

    exportCompaniesDataAsExcel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.exportCompaniesDataAsExcel.do.type),
            mergeMap(
                (
                    action: fromActions.ActionWithPayload<{
                        tableHeaders: ICompanyExportTableHeaders;
                        companies: ICompanyExportDto[];
                        fileName: string;
                    }>,
                ) => {
                    return this.service.exportCompaniesDataAsExcel$(action.payload).pipe(
                        map((response) => {
                            const blob = new Blob([response], {
                                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                            });
                            const downloadLink = document.createElement('a');
                            downloadLink.href = window.URL.createObjectURL(blob);
                            downloadLink.download = action.payload.fileName;
                            downloadLink.click();
                            window.URL.revokeObjectURL(downloadLink.href);
                            return fromActions.exportCompaniesDataAsExcel.success();
                        }),
                        catchError((error) => {
                            return of(
                                fromActions.exportCompaniesDataAsExcel.failed({
                                    payload: error,
                                }),
                            );
                        }),
                    );
                },
            ),
        ),
    );
}
